import React, { useState, useEffect } from "react";
import {
  Typography,
  Button,
  Box,
  Grid,
  Container,
  Chip,
  Paper,
  Stack,
  Avatar,
  Fade,
  Grow,
} from "@mui/material";
import {
  ContactMail,
  Groups,
  Schedule,
  Segment,
  Insights,
  Notifications,
  PersonAdd,
  Email,
  Phone,
  ArrowForward,
  Star,
  BusinessCenter,
  IntegrationInstructions,
  BarChart,
  TrendingUp,
  CheckCircle,
  AutoAwesome,
  Bolt,
  Favorite,
  Speed,
  EmojiEvents,
  PlayArrow,
} from "@mui/icons-material";
import Footer from "./Footer";

export const LandingPage = () => {
  // Live stats for contact management
  const [liveStats, setLiveStats] = useState({
    contacts: 25847,
    interactions: 8421,
    activeContacts: 1892,
    satisfaction: "98%",
  });

  // Simulate live updates
  useEffect(() => {
    const interval = setInterval(() => {
      setLiveStats((prev) => ({
        ...prev,
        contacts: prev.contacts + Math.floor(Math.random() * 5),
        interactions: prev.interactions + Math.floor(Math.random() * 3),
      }));
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const workflows = [
    {
      step: "01",
      title: "Import & Organize",
      description: "Import contacts from any source and automatically organize with smart tags and segments",
      icon: <PersonAdd sx={{ fontSize: 32 }} />,
      color: "#0ea5e9",
    },
    {
      step: "02",
      title: "Enrich & Update",
      description: "Auto-enrich profiles with social data, company info, and real-time updates",
      icon: <BusinessCenter sx={{ fontSize: 32 }} />,
      color: "#8b5cf6",
    },
    {
      step: "03",
      title: "Track & Engage",
      description: "Log every interaction automatically and engage with personalized communication",
      icon: <Email sx={{ fontSize: 32 }} />,
      color: "#10b981",
    },
    {
      step: "04",
      title: "Analyze & Grow",
      description: "Gain insights into relationships and grow your network strategically",
      icon: <Insights sx={{ fontSize: 32 }} />,
      color: "#f59e0b",
    },
  ];

  const testimonials = [
    {
      name: "Sarah Thompson",
      role: "Account Manager",
      company: "Growth Solutions",
      rating: 5,
      text: "This contact management system transformed how we maintain relationships. We've increased client retention by 45%!",
      avatar: "ST",
    },
    {
      name: "David Liu",
      role: "Sales Director",
      company: "TechVentures",
      rating: 5,
      text: "Best investment we've made. The interaction tracking and reminders ensure no contact falls through the cracks.",
      avatar: "DL",
    },
    {
      name: "Maria Garcia",
      role: "CEO",
      company: "Innovate Inc.",
      rating: 5,
      text: "Our team productivity tripled! Having all contact information and history in one place is a game-changer.",
      avatar: "MG",
    },
  ];

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#fafafa" }}>
      {/* Modern Hero Section */}
      <Box
        sx={{
          minHeight: { xs: "auto", md: "100vh" },
          background: "linear-gradient(135deg, #0a0e27 0%, #1a1f3a 25%, #2d1b4e 50%, #1e293b 75%, #0f172a 100%)",
          position: "relative",
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          py: { xs: 8, sm: 10, md: 0 },
          px: { xs: 2, sm: 3 },
        }}
      >
        {/* Animated Grid Background */}
        <Box
          sx={{
            position: "absolute",
            width: "100%",
            height: "100%",
            backgroundImage: "linear-gradient(rgba(99,102,241,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(99,102,241,0.03) 1px, transparent 1px)",
            backgroundSize: "50px 50px",
            opacity: 0.4,
          }}
        />

        {/* Animated Gradient Orbs */}
        <Box
          sx={{
            position: "absolute",
            width: 700,
            height: 700,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(99,102,241,0.4) 0%, rgba(14,165,233,0.2) 40%, transparent 70%)",
            top: "-20%",
            right: "-10%",
            animation: "float 20s infinite ease-in-out",
            filter: "blur(60px)",
            "@keyframes float": {
              "0%, 100%": { transform: "translate(0, 0) scale(1)" },
              "50%": { transform: "translate(-50px, 50px) scale(1.15)" },
            },
          }}
        />
        <Box
          sx={{
            position: "absolute",
            width: 500,
            height: 500,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(139,92,246,0.3) 0%, rgba(217,70,239,0.2) 40%, transparent 70%)",
            bottom: "-10%",
            left: "-5%",
            animation: "float 15s infinite ease-in-out reverse",
            filter: "blur(60px)",
          }}
        />

        {/* Floating Particles */}
        {[...Array(50)].map((_, i) => (
          <Box
            key={i}
            sx={{
              position: "absolute",
              width: `${Math.random() * 4 + 2}px`,
              height: `${Math.random() * 4 + 2}px`,
              borderRadius: "50%",
              background: `rgba(${Math.random() > 0.5 ? '99,102,241' : '139,92,246'}, ${Math.random() * 0.5 + 0.3})`,
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animation: `twinkle ${Math.random() * 5 + 3}s infinite ease-in-out`,
              animationDelay: `${Math.random() * 3}s`,
              "@keyframes twinkle": {
                "0%, 100%": { opacity: 0.2, transform: "translateY(0px)" },
                "50%": { opacity: 1, transform: `translateY(${Math.random() * 20 - 10}px)` },
              },
            }}
          />
        ))}

        <Container sx={{ position: "relative", zIndex: 2 }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={7}>
              <Fade in timeout={800}>
                <Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 2 }, mb: { xs: 2, sm: 3 }, flexWrap: "wrap" }}>
                    <Chip
                      icon={<AutoAwesome sx={{ color: "#fbbf24 !important", fontSize: { xs: 16, sm: 18 } }} />}
                      label="🚀 Trusted by 25,000+ Teams"
                      sx={{
                        bgcolor: "rgba(251,191,36,0.15)",
                        color: "#fbbf24",
                        fontWeight: 700,
                        backdropFilter: "blur(10px)",
                        border: "1px solid rgba(251,191,36,0.3)",
                        py: { xs: 1.5, sm: 2.5 },
                        px: { xs: 1.5, sm: 2 },
                        fontSize: { xs: "0.8rem", sm: "0.95rem" },
                      }}
                    />
                    <Chip
                      label="✨ New AI Features"
                      size="small"
                      sx={{
                        bgcolor: "#10b981",
                        color: "white",
                        fontWeight: 700,
                        px: { xs: 1, sm: 1.5 },
                        fontSize: { xs: "0.75rem", sm: "0.8rem" },
                        animation: "pulse 2s infinite",
                        "@keyframes pulse": {
                          "0%, 100%": { opacity: 1, transform: "scale(1)" },
                          "50%": { opacity: 0.85, transform: "scale(1.05)" },
                        },
                      }}
                    />
                  </Box>
                  
                  <Typography
                    variant="h1"
                    sx={{
                      fontSize: { xs: "2.2rem", sm: "3.5rem", md: "5.5rem", lg: "6.5rem" },
                      fontWeight: 900,
                      color: "white",
                      lineHeight: { xs: 1.1, md: 1.05 },
                      mb: { xs: 2, sm: 3 },
                      letterSpacing: { xs: "-0.02em", md: "-0.03em" },
                    }}
                  >
                    Your Contacts,
                    <Box
                      component="span"
                      sx={{
                        display: "block",
                        background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 40%, #d946ef 70%, #0ea5e9 100%)",
                        backgroundClip: "text",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        position: "relative",
                        backgroundSize: "200% 100%",
                        animation: "gradientShift 3s ease infinite",
                        "@keyframes gradientShift": {
                          "0%, 100%": { backgroundPosition: "0% 50%" },
                          "50%": { backgroundPosition: "100% 50%" },
                        },
                      }}
                    >
                      Supercharged
                      <Box
                        component="span"
                        sx={{
                          position: "absolute",
                          bottom: 0,
                          left: 0,
                          right: 0,
                          height: { xs: 4, sm: 6, md: 8 },
                          background: "linear-gradient(90deg, #6366f1, #d946ef, transparent)",
                          opacity: 0.4,
                          borderRadius: 3,
                        }}
                      />
                    </Box>
                  </Typography>

                  <Typography
                    variant="h5"
                    sx={{
                      color: "rgba(255,255,255,0.85)",
                      lineHeight: { xs: 1.6, md: 1.7 },
                      mb: { xs: 3, sm: 4, md: 5 },
                      maxWidth: 600,
                      fontSize: { xs: "0.95rem", sm: "1.1rem", md: "1.45rem" },
                      fontWeight: 400,
                    }}
                  >
                    The AI-powered contact management platform that helps teams build stronger relationships, 
                    close more deals, and never miss a follow-up. <Box component="span" sx={{ color: "#10b981", fontWeight: 600 }}>3x faster than spreadsheets.</Box>
                  </Typography>

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: { xs: 4, sm: 5, md: 6 } }}>
                    <Button
                      variant="contained"
                      size="large"
                      endIcon={<ArrowForward sx={{ fontSize: { xs: 18, sm: 20 } }} />}
                      onClick={() => window.open('https://www.orucore.com', '_blank')}
                      sx={{
                        background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                        color: "white",
                        px: { xs: 4, sm: 5, md: 6 },
                        py: { xs: 1.8, sm: 2, md: 2.5 },
                        fontSize: { xs: "0.95rem", sm: "1.05rem", md: "1.15rem" },
                        fontWeight: 800,
                        borderRadius: 2,
                        textTransform: "none",
                        boxShadow: "0 20px 60px rgba(99,102,241,0.5)",
                        position: "relative",
                        overflow: "hidden",
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          top: 0,
                          left: "-100%",
                          width: "100%",
                          height: "100%",
                          background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)",
                          transition: "left 0.5s",
                        },
                        "&:hover": {
                          transform: "translateY(-3px)",
                          boxShadow: "0 25px 80px rgba(99,102,241,0.6)",
                          "&::before": {
                            left: "100%",
                          },
                        },
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      }}
                    >
                      Start Free Trial
                    </Button>
                    <Button
                      variant="outlined"
                      size="large"
                      startIcon={<PlayArrow sx={{ fontSize: { xs: 18, sm: 20 } }} />}
                      onClick={() => window.open('https://www.orucore.com', '_blank')}
                      sx={{
                        borderColor: "rgba(255,255,255,0.3)",
                        color: "white",
                        px: { xs: 4, sm: 5, md: 6 },
                        py: { xs: 1.8, sm: 2, md: 2.5 },
                        fontSize: { xs: "0.95rem", sm: "1.05rem", md: "1.15rem" },
                        fontWeight: 700,
                        borderRadius: 2,
                        textTransform: "none",
                        backdropFilter: "blur(10px)",
                        bgcolor: "rgba(255,255,255,0.05)",
                        borderWidth: 2,
                        "&:hover": {
                          borderColor: "#6366f1",
                          bgcolor: "rgba(99,102,241,0.15)",
                          borderWidth: 2,
                          transform: "translateY(-3px)",
                        },
                        transition: "all 0.3s",
                      }}
                    >
                      Watch Demo
                    </Button>
                  </Stack>

                  <Stack direction="row" spacing={{ xs: 2, sm: 3, md: 4 }} flexWrap="wrap" sx={{ mb: { xs: 3, sm: 4, md: 5 }, gap: { xs: 1, sm: 0 } }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.8, sm: 1 } }}>
                      <CheckCircle sx={{ color: "#10b981", fontSize: { xs: 18, sm: 20, md: 22 } }} />
                      <Typography sx={{ color: "rgba(255,255,255,0.85)", fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1rem" }, fontWeight: 500 }}>
                        Free 14-day trial
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.8, sm: 1 } }}>
                      <CheckCircle sx={{ color: "#10b981", fontSize: { xs: 18, sm: 20, md: 22 } }} />
                      <Typography sx={{ color: "rgba(255,255,255,0.85)", fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1rem" }, fontWeight: 500 }}>
                        No credit card
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.8, sm: 1 } }}>
                      <CheckCircle sx={{ color: "#10b981", fontSize: { xs: 18, sm: 20, md: 22 } }} />
                      <Typography sx={{ color: "rgba(255,255,255,0.85)", fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1rem" }, fontWeight: 500 }}>
                        Cancel anytime
                      </Typography>
                    </Box>
                  </Stack>

                  {/* Trust Badges */}
                  <Box sx={{ display: { xs: "none", sm: "block" } }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: "rgba(255,255,255,0.5)", 
                        mb: 2,
                        textTransform: "uppercase",
                        letterSpacing: "0.1em",
                        fontSize: { xs: "0.65rem", sm: "0.75rem" },
                        fontWeight: 600,
                      }}
                    >
                      Trusted by leading companies
                    </Typography>
                    <Stack direction="row" spacing={{ xs: 1, sm: 2, md: 3 }} alignItems="center" flexWrap="wrap" sx={{ gap: { xs: 1, sm: 1.5, md: 2 } }}>
                      {['Microsoft', 'Salesforce', 'HubSpot', 'Slack', 'Zoom'].map((company) => (
                        <Chip
                          key={company}
                          label={company}
                          size="small"
                          sx={{
                            bgcolor: "rgba(255,255,255,0.08)",
                            color: "rgba(255,255,255,0.7)",
                            backdropFilter: "blur(10px)",
                            border: "1px solid rgba(255,255,255,0.1)",
                            fontWeight: 600,
                            fontSize: { xs: "0.75rem", sm: "0.85rem" },
                            "&:hover": {
                              bgcolor: "rgba(255,255,255,0.12)",
                            },
                          }}
                        />
                      ))}
                    </Stack>
                  </Box>
                </Box>
              </Fade>
            </Grid>

            <Grid item xs={12} md={5}>
              <Grow in timeout={1000}>
                <Box sx={{ mt: { xs: 4, md: 0 } }}>
                  {/* Enhanced Bento Grid Stats */}
                  <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                    <Grid item xs={6}>
                      <Paper
                        sx={{
                          p: { xs: 2, sm: 2.5, md: 3 },
                          background: "linear-gradient(135deg, rgba(99,102,241,0.15) 0%, rgba(139,92,246,0.1) 100%)",
                          backdropFilter: "blur(20px)",
                          border: "1px solid rgba(99,102,241,0.3)",
                          borderRadius: { xs: 2, sm: 3 },
                          position: "relative",
                          overflow: "hidden",
                          transition: "all 0.3s",
                          "&:hover": {
                            background: "linear-gradient(135deg, rgba(99,102,241,0.25) 0%, rgba(139,92,246,0.2) 100%)",
                            transform: "translateY(-6px)",
                            borderColor: "rgba(99,102,241,0.5)",
                          },
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            height: "3px",
                            background: "linear-gradient(90deg, #6366f1, #8b5cf6)",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            width: { xs: 40, sm: 45, md: 50 },
                            height: { xs: 40, sm: 45, md: 50 },
                            borderRadius: 2,
                            background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mb: { xs: 1.5, sm: 2 },
                            boxShadow: "0 8px 24px rgba(99,102,241,0.3)",
                          }}
                        >
                          <ContactMail sx={{ color: "white", fontSize: { xs: 22, sm: 26, md: 28 } }} />
                        </Box>
                        <Typography variant="h3" sx={{ color: "white", fontWeight: 900, mb: 0.5, fontSize: { xs: "1.8rem", sm: "2.2rem", md: "3rem" } }}>
                          {liveStats.contacts.toLocaleString()}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)", fontWeight: 600, fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.85rem" } }}>
                          Contacts Managed
                        </Typography>
                        <Chip
                          label="+127 today"
                          size="small"
                          sx={{
                            mt: { xs: 0.5, sm: 1 },
                            bgcolor: "rgba(16,185,129,0.2)",
                            color: "#10b981",
                            fontSize: { xs: "0.65rem", sm: "0.7rem" },
                            height: { xs: "18px", sm: "20px" },
                            fontWeight: 700,
                          }}
                        />
                      </Paper>
                    </Grid>
                    <Grid item xs={6}>
                      <Paper
                        sx={{
                          p: { xs: 2, sm: 2.5, md: 3 },
                          background: "linear-gradient(135deg, rgba(14,165,233,0.15) 0%, rgba(6,182,212,0.1) 100%)",
                          backdropFilter: "blur(20px)",
                          border: "1px solid rgba(14,165,233,0.3)",
                          borderRadius: { xs: 2, sm: 3 },
                          position: "relative",
                          overflow: "hidden",
                          transition: "all 0.3s",
                          "&:hover": {
                            background: "linear-gradient(135deg, rgba(14,165,233,0.25) 0%, rgba(6,182,212,0.2) 100%)",
                            transform: "translateY(-6px)",
                            borderColor: "rgba(14,165,233,0.5)",
                          },
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            height: "3px",
                            background: "linear-gradient(90deg, #0ea5e9, #06b6d4)",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            width: { xs: 40, sm: 45, md: 50 },
                            height: { xs: 40, sm: 45, md: 50 },
                            borderRadius: 2,
                            background: "linear-gradient(135deg, #0ea5e9, #06b6d4)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mb: { xs: 1.5, sm: 2 },
                            boxShadow: "0 8px 24px rgba(14,165,233,0.3)",
                          }}
                        >
                          <Groups sx={{ color: "white", fontSize: { xs: 22, sm: 26, md: 28 } }} />
                        </Box>
                        <Typography variant="h3" sx={{ color: "white", fontWeight: 900, mb: 0.5, fontSize: { xs: "1.8rem", sm: "2.2rem", md: "3rem" } }}>
                          25k+
                        </Typography>
                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)", fontWeight: 600, fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.85rem" } }}>
                          Active Teams
                        </Typography>
                        <Chip
                          label="Growing"
                          size="small"
                          sx={{
                            mt: { xs: 0.5, sm: 1 },
                            bgcolor: "rgba(99,102,241,0.2)",
                            color: "#6366f1",
                            fontSize: { xs: "0.65rem", sm: "0.7rem" },
                            height: { xs: "18px", sm: "20px" },
                            fontWeight: 700,
                          }}
                        />
                      </Paper>
                    </Grid>
                    <Grid item xs={12}>
                      <Paper
                        sx={{
                          p: { xs: 2.5, sm: 3, md: 4 },
                          background: "linear-gradient(135deg, rgba(251,191,36,0.15) 0%, rgba(245,158,11,0.1) 100%)",
                          backdropFilter: "blur(20px)",
                          border: "1px solid rgba(251,191,36,0.3)",
                          borderRadius: { xs: 2, sm: 3 },
                          position: "relative",
                          overflow: "hidden",
                          transition: "all 0.3s",
                          "&:hover": {
                            background: "linear-gradient(135deg, rgba(251,191,36,0.25) 0%, rgba(245,158,11,0.2) 100%)",
                            transform: "translateY(-6px)",
                            borderColor: "rgba(251,191,36,0.5)",
                          },
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            height: "3px",
                            background: "linear-gradient(90deg, #fbbf24, #f59e0b)",
                          },
                        }}
                      >
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: { xs: 1.5, sm: 2 }, gap: 2 }}>
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, sm: 1 }, mb: 1, flexWrap: { xs: "wrap", sm: "nowrap" } }}>
                              <Typography variant="h2" sx={{ color: "white", fontWeight: 900, fontSize: { xs: "2.5rem", sm: "3rem", md: "3.5rem" } }}>
                                4.9
                              </Typography>
                              <Box>
                                <Box sx={{ display: "flex", gap: { xs: 0.2, sm: 0.3 } }}>
                                  {[1, 2, 3, 4, 5].map((star) => (
                                    <Star key={star} sx={{ color: "#fbbf24", fontSize: { xs: 18, sm: 20, md: 22 } }} />
                                  ))}
                                </Box>
                                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)", fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" } }}>
                                  from 2,847 reviews
                                </Typography>
                              </Box>
                            </Box>
                            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)", fontWeight: 600, fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" } }}>
                              Rated #1 Contact Platform 2026
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              width: { xs: 50, sm: 60, md: 70 },
                              height: { xs: 50, sm: 60, md: 70 },
                              borderRadius: "50%",
                              background: "linear-gradient(135deg, #fbbf24, #f59e0b)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              boxShadow: "0 8px 24px rgba(251,191,36,0.4)",
                            }}
                          >
                            <EmojiEvents sx={{ color: "white", fontSize: { xs: 28, sm: 34, md: 40 } }} />
                          </Box>
                        </Box>
                      </Paper>
                    </Grid>
                  </Grid>
                </Box>
              </Grow>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Modern Features Section - Bento Grid Style */}
      <Container sx={{ py: { xs: 10, md: 15 }, px: { xs: 2, sm: 3 } }}>
        <Box sx={{ textAlign: "center", mb: { xs: 6, sm: 8, md: 10 } }}>
          <Chip
            icon={<Speed sx={{ fontSize: { xs: 16, sm: 18 } }} />}
            label="POWERFUL FEATURES"
            sx={{
              bgcolor: "#f0f9ff",
              color: "#0284c7",
              fontWeight: 700,
              mb: { xs: 2, sm: 3 },
              border: "2px solid #bae6fd",
              px: { xs: 1.5, sm: 2 },
              py: { xs: 2, sm: 2.5 },
              fontSize: { xs: "0.75rem", sm: "0.85rem" },
            }}
          />
          <Typography
            variant="h2"
            sx={{
              fontWeight: 900,
              mb: { xs: 1.5, sm: 2 },
              color: "#0f172a",
              fontSize: { xs: "2rem", sm: "3rem", md: "4rem" },
              letterSpacing: "-0.02em",
              px: { xs: 2, sm: 0 },
            }}
          >
            Built for Modern Teams
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: "#64748b",
              maxWidth: 700,
              mx: "auto",
              fontWeight: 400,
              fontSize: { xs: "0.95rem", sm: "1.1rem", md: "1.3rem" },
              px: { xs: 2, sm: 0 },
            }}
          >
            Everything you need to manage contacts smarter, not harder
          </Typography>
        </Box>

        {/* Bento Grid Layout */}
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {/* Large Feature Card - Unified Hub */}
          <Grid item xs={12} md={8}>
            <Paper
              elevation={0}
              sx={{
                height: { xs: 280, sm: 340, md: 420 },
                borderRadius: { xs: 3, sm: 4 },
                background: "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)",
                p: { xs: 3, sm: 4, md: 5 },
                position: "relative",
                overflow: "hidden",
                border: "1px solid rgba(255,255,255,0.1)",
                transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  transform: "translateY(-8px)",
                  boxShadow: "0 30px 80px rgba(14,165,233,0.3)",
                },
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: -50,
                  right: -50,
                  width: 300,
                  height: 300,
                  borderRadius: "50%",
                  background: "rgba(255,255,255,0.1)",
                }}
              />
              <Box sx={{ position: "relative", zIndex: 1 }}>
                <Box
                  sx={{
                    width: { xs: 60, sm: 70, md: 80 },
                    height: { xs: 60, sm: 70, md: 80 },
                    borderRadius: { xs: 2, sm: 3 },
                    bgcolor: "rgba(255,255,255,0.2)",
                    backdropFilter: "blur(10px)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: { xs: 2, sm: 3 },
                  }}
                >
                  <Groups sx={{ fontSize: { xs: 36, sm: 44, md: 50 }, color: "white" }} />
                </Box>
                <Typography variant="h3" sx={{ color: "white", fontWeight: 900, mb: { xs: 1.5, sm: 2 }, fontSize: { xs: "1.5rem", sm: "2rem", md: "3rem" } }}>
                  Unified Contact Hub
                </Typography>
                <Typography variant="h6" sx={{ color: "rgba(255,255,255,0.9)", lineHeight: 1.6, fontWeight: 400, fontSize: { xs: "0.9rem", sm: "1.1rem", md: "1.25rem" } }}>
                  Centralize all your contacts in one intelligent, searchable database with complete interaction history and smart organization
                </Typography>
                <Box sx={{ mt: { xs: 2, sm: 3, md: 4 }, display: { xs: "none", sm: "block" } }}>
                  <Chip
                    label="All-in-One Platform"
                    sx={{
                      bgcolor: "rgba(255,255,255,0.2)",
                      color: "white",
                      backdropFilter: "blur(10px)",
                      fontWeight: 700,
                      border: "1px solid rgba(255,255,255,0.3)",
                    }}
                  />
                </Box>
              </Box>
            </Paper>
          </Grid>

          {/* Medium Feature Card - Smart Segmentation */}
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                height: { xs: 280, sm: 340, md: 420 },
                borderRadius: { xs: 3, sm: 4 },
                background: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
                p: { xs: 3, sm: 4 },
                position: "relative",
                overflow: "hidden",
                border: "1px solid rgba(255,255,255,0.1)",
                transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  transform: "translateY(-8px)",
                  boxShadow: "0 30px 80px rgba(139,92,246,0.3)",
                },
              }}
            >
              <Box
                sx={{
                  width: 70,
                  height: 70,
                  borderRadius: 3,
                  bgcolor: "rgba(255,255,255,0.2)",
                  backdropFilter: "blur(10px)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 3,
                }}
              >
                <Segment sx={{ fontSize: 42, color: "white" }} />
              </Box>
              <Typography variant="h4" sx={{ color: "white", fontWeight: 900, mb: 2 }}>
                Smart Segmentation
              </Typography>
              <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.9)", lineHeight: 1.7, fontSize: "1.05rem" }}>
                Organize contacts with custom tags, groups, and AI-powered smart segments
              </Typography>
              <Box sx={{ mt: 3 }}>
                <Chip
                  label="AI-Powered"
                  icon={<AutoAwesome sx={{ color: "white !important" }} />}
                  sx={{
                    bgcolor: "rgba(255,255,255,0.2)",
                    color: "white",
                    fontWeight: 700,
                  }}
                />
              </Box>
            </Paper>
          </Grid>

          {/* Small Cards Row */}
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                height: { xs: 220, sm: 260, md: 300 },
                borderRadius: { xs: 3, sm: 4 },
                background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                p: { xs: 3, sm: 4 },
                position: "relative",
                overflow: "hidden",
                transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  transform: "translateY(-8px)",
                  boxShadow: "0 30px 80px rgba(16,185,129,0.3)",
                },
              }}
            >
              <Schedule sx={{ fontSize: { xs: 45, sm: 55, md: 60 }, color: "white", mb: { xs: 1.5, sm: 2 } }} />
              <Typography variant="h5" sx={{ color: "white", fontWeight: 900, mb: { xs: 1, sm: 1.5 }, fontSize: { xs: "1.1rem", sm: "1.3rem", md: "1.5rem" } }}>
                Interaction Tracking
              </Typography>
              <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.9)", lineHeight: 1.7, fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1rem" } }}>
                Track every email, call, and meeting automatically
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                height: { xs: 220, sm: 260, md: 300 },
                borderRadius: { xs: 3, sm: 4 },
                background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                p: { xs: 3, sm: 4 },
                position: "relative",
                overflow: "hidden",
                transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  transform: "translateY(-8px)",
                  boxShadow: "0 30px 80px rgba(245,158,11,0.3)",
                },
              }}
            >
              <Notifications sx={{ fontSize: { xs: 45, sm: 55, md: 60 }, color: "white", mb: { xs: 1.5, sm: 2 } }} />
              <Typography variant="h5" sx={{ color: "white", fontWeight: 900, mb: { xs: 1, sm: 1.5 }, fontSize: { xs: "1.1rem", sm: "1.3rem", md: "1.5rem" } }}>
                Smart Reminders
              </Typography>
              <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.9)", lineHeight: 1.7, fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1rem" } }}>
                Never miss a follow-up with intelligent automation
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                height: { xs: 220, sm: 260, md: 300 },
                borderRadius: { xs: 3, sm: 4 },
                background: "linear-gradient(135deg, #ec4899 0%, #db2777 100%)",
                p: { xs: 3, sm: 4 },
                position: "relative",
                overflow: "hidden",
                transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  transform: "translateY(-8px)",
                  boxShadow: "0 30px 80px rgba(236,72,153,0.3)",
                },
              }}
            >
              <BarChart sx={{ fontSize: { xs: 45, sm: 55, md: 60 }, color: "white", mb: { xs: 1.5, sm: 2 } }} />
              <Typography variant="h5" sx={{ color: "white", fontWeight: 900, mb: { xs: 1, sm: 1.5 }, fontSize: { xs: "1.1rem", sm: "1.3rem", md: "1.5rem" } }}>
                Contact Analytics
              </Typography>
              <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.9)", lineHeight: 1.7, fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1rem" } }}>
                Understand relationships with powerful insights
              </Typography>
            </Paper>
          </Grid>

          {/* Bottom Wide Card - Integrations */}
          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: { xs: 3, sm: 4 },
                background: "linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)",
                p: { xs: 3, sm: 4, md: 5 },
                position: "relative",
                overflow: "hidden",
                transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  transform: "translateY(-8px)",
                  boxShadow: "0 30px 80px rgba(6,182,212,0.3)",
                },
              }}
            >
              <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} alignItems="center">
                <Grid item xs={12} md={8}>
                  <Box
                    sx={{
                      width: { xs: 55, sm: 65, md: 70 },
                      height: { xs: 55, sm: 65, md: 70 },
                      borderRadius: { xs: 2, sm: 3 },
                      bgcolor: "rgba(255,255,255,0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: { xs: 1.5, sm: 2 },
                    }}
                  >
                    <IntegrationInstructions sx={{ fontSize: { xs: 32, sm: 38, md: 42 }, color: "white" }} />
                  </Box>
                  <Typography variant="h4" sx={{ color: "white", fontWeight: 900, mb: { xs: 1.5, sm: 2 }, fontSize: { xs: "1.3rem", sm: "1.8rem", md: "2.125rem" } }}>
                    Seamless Integrations
                  </Typography>
                  <Typography variant="h6" sx={{ color: "rgba(255,255,255,0.9)", lineHeight: 1.6, fontWeight: 400, fontSize: { xs: "0.95rem", sm: "1.1rem", md: "1.25rem" } }}>
                    Connect with Gmail, Outlook, Calendar, Slack, Zoom, and 100+ apps you already use daily
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: { xs: 1, sm: 1.5 }, justifyContent: { xs: "flex-start", md: "flex-end" } }}>
                    <Chip label="Gmail" sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white", fontWeight: 600, fontSize: { xs: "0.75rem", sm: "0.85rem" } }} />
                    <Chip label="Outlook" sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white", fontWeight: 600, fontSize: { xs: "0.75rem", sm: "0.85rem" } }} />
                    <Chip label="Slack" sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white", fontWeight: 600, fontSize: { xs: "0.75rem", sm: "0.85rem" } }} />
                    <Chip label="Zoom" sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white", fontWeight: 600, fontSize: { xs: "0.75rem", sm: "0.85rem" } }} />
                    <Chip label="+100 more" sx={{ bgcolor: "rgba(255,255,255,0.3)", color: "white", fontWeight: 700, fontSize: { xs: "0.75rem", sm: "0.85rem" } }} />
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Modern Benefits Section */}
      <Box sx={{ bgcolor: "#0f172a", py: { xs: 10, md: 15 }, position: "relative", overflow: "hidden" }}>
        {/* Background Elements */}
        <Box
          sx={{
            position: "absolute",
            width: 500,
            height: 500,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(14,165,233,0.15) 0%, transparent 70%)",
            top: "-10%",
            left: "-10%",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            width: 400,
            height: 400,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(139,92,246,0.1) 0%, transparent 70%)",
            bottom: "-5%",
            right: "-5%",
          }}
        />

        <Container sx={{ position: "relative", zIndex: 1 }}>
          <Box sx={{ textAlign: "center", mb: 10 }}>
            <Chip
              icon={<TrendingUp sx={{ fontSize: 18, color: "#10b981 !important" }} />}
              label="PROVEN RESULTS"
              sx={{
                bgcolor: "rgba(16,185,129,0.2)",
                color: "#10b981",
                fontWeight: 700,
                mb: 3,
                border: "2px solid rgba(16,185,129,0.3)",
                px: 2,
                py: 2.5,
              }}
            />
            <Typography
              variant="h2"
              sx={{
                fontWeight: 900,
                mb: 2,
                color: "white",
                fontSize: { xs: "2.5rem", md: "4rem" },
                letterSpacing: "-0.02em",
              }}
            >
              Results That Speak
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: "rgba(255,255,255,0.7)",
                maxWidth: 700,
                mx: "auto",
                fontWeight: 400,
                fontSize: { xs: "1.1rem", md: "1.3rem" },
              }}
            >
              Join industry leaders who've transformed their contact management
            </Typography>
          </Box>

          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Paper
                sx={{
                  p: 5,
                  borderRadius: 4,
                  background: "linear-gradient(135deg, rgba(14,165,233,0.1) 0%, rgba(6,182,212,0.1) 100%)",
                  backdropFilter: "blur(20px)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  height: "100%",
                  transition: "all 0.4s",
                  "&:hover": {
                    transform: "translateY(-8px)",
                    background: "linear-gradient(135deg, rgba(14,165,233,0.15) 0%, rgba(6,182,212,0.15) 100%)",
                  },
                }}
              >
                <Speed sx={{ fontSize: 60, color: "#0ea5e9", mb: 3 }} />
                <Typography variant="h3" sx={{ color: "white", fontWeight: 900, mb: 2 }}>
                  45% Faster
                </Typography>
                <Typography variant="h6" sx={{ color: "rgba(255,255,255,0.8)", lineHeight: 1.7, fontWeight: 400 }}>
                  Teams respond to contacts 45% faster with automated reminders and smart notifications
                </Typography>
                <Box sx={{ mt: 4, pt: 4, borderTop: "1px solid rgba(255,255,255,0.1)" }}>
                  <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.6)" }}>
                    Average response time improvement
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper
                sx={{
                  p: 5,
                  borderRadius: 4,
                  background: "linear-gradient(135deg, rgba(139,92,246,0.1) 0%, rgba(124,58,237,0.1) 100%)",
                  backdropFilter: "blur(20px)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  height: "100%",
                  transition: "all 0.4s",
                  "&:hover": {
                    transform: "translateY(-8px)",
                    background: "linear-gradient(135deg, rgba(139,92,246,0.15) 0%, rgba(124,58,237,0.15) 100%)",
                  },
                }}
              >
                <EmojiEvents sx={{ fontSize: 60, color: "#8b5cf6", mb: 3 }} />
                <Typography variant="h3" sx={{ color: "white", fontWeight: 900, mb: 2 }}>
                  3x Better
                </Typography>
                <Typography variant="h6" sx={{ color: "rgba(255,255,255,0.8)", lineHeight: 1.7, fontWeight: 400 }}>
                  Organizations report 3x better contact organization and relationship tracking
                </Typography>
                <Box sx={{ mt: 4, pt: 4, borderTop: "1px solid rgba(255,255,255,0.1)" }}>
                  <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.6)" }}>
                    Organization efficiency increase
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper
                sx={{
                  p: 4,
                  borderRadius: 4,
                  bgcolor: "rgba(255,255,255,0.05)",
                  backdropFilter: "blur(20px)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  textAlign: "center",
                  transition: "all 0.3s",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    bgcolor: "rgba(255,255,255,0.08)",
                  },
                }}
              >
                <Typography variant="h2" sx={{ color: "#10b981", fontWeight: 900, mb: 1 }}>
                  60%
                </Typography>
                <Typography variant="h6" sx={{ color: "white", fontWeight: 700, mb: 1 }}>
                  Time Saved Daily
                </Typography>
                <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.6)" }}>
                  On contact management tasks
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper
                sx={{
                  p: 4,
                  borderRadius: 4,
                  bgcolor: "rgba(255,255,255,0.05)",
                  backdropFilter: "blur(20px)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  textAlign: "center",
                  transition: "all 0.3s",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    bgcolor: "rgba(255,255,255,0.08)",
                  },
                }}
              >
                <Typography variant="h2" sx={{ color: "#f59e0b", fontWeight: 900, mb: 1 }}>
                  99.9%
                </Typography>
                <Typography variant="h6" sx={{ color: "white", fontWeight: 700, mb: 1 }}>
                  Uptime Guarantee
                </Typography>
                <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.6)" }}>
                  Always available when you need it
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper
                sx={{
                  p: 4,
                  borderRadius: 4,
                  bgcolor: "rgba(255,255,255,0.05)",
                  backdropFilter: "blur(20px)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  textAlign: "center",
                  transition: "all 0.3s",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    bgcolor: "rgba(255,255,255,0.08)",
                  },
                }}
              >
                <Typography variant="h2" sx={{ color: "#ec4899", fontWeight: 900, mb: 1 }}>
                  98%
                </Typography>
                <Typography variant="h6" sx={{ color: "white", fontWeight: 700, mb: 1 }}>
                  Client Satisfaction
                </Typography>
                <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.6)" }}>
                  Rated by 25,000+ users
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Modern How It Works Section */}
      <Container sx={{ py: { xs: 10, md: 15 } }}>
        <Box sx={{ textAlign: "center", mb: 10 }}>
          <Chip
            icon={<Bolt sx={{ fontSize: 18, color: "#f59e0b !important" }} />}
            label="SIMPLE WORKFLOW"
            sx={{
              bgcolor: "#fef3c7",
              color: "#f59e0b",
              fontWeight: 700,
              mb: 3,
              border: "2px solid #fde68a",
              px: 2,
              py: 2.5,
            }}
          />
          <Typography
            variant="h2"
            sx={{
              fontWeight: 900,
              mb: 2,
              color: "#0f172a",
              fontSize: { xs: "2.5rem", md: "4rem" },
              letterSpacing: "-0.02em",
            }}
          >
            Get Started in Minutes
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: "#64748b",
              maxWidth: 700,
              mx: "auto",
              fontWeight: 400,
              fontSize: { xs: "1.1rem", md: "1.3rem" },
            }}
          >
            From import to insights  - streamlined and automated
          </Typography>
        </Box>

        <Box sx={{ position: "relative" }}>
          {/* Connection Line */}
          <Box
            sx={{
              display: { xs: "none", md: "block" },
              position: "absolute",
              top: 80,
              left: "12.5%",
              right: "12.5%",
              height: 4,
              background: "linear-gradient(90deg, #0ea5e9, #8b5cf6, #10b981, #f59e0b)",
              borderRadius: 2,
              opacity: 0.2,
              zIndex: 0,
            }}
          />

          <Grid container spacing={4}>
            {workflows.map((workflow, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Box
                  sx={{
                    position: "relative",
                    zIndex: 1,
                    textAlign: "center",
                    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      transform: "translateY(-12px)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 100,
                      height: 100,
                      borderRadius: "50%",
                      background: `linear-gradient(135deg, ${workflow.color}, ${workflow.color}dd)`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mx: "auto",
                      mb: 3,
                      boxShadow: `0 20px 60px ${workflow.color}40`,
                      position: "relative",
                      transition: "all 0.4s",
                      "&:hover": {
                        transform: "scale(1.1)",
                        boxShadow: `0 30px 80px ${workflow.color}60`,
                      },
                    }}
                  >
                    {workflow.icon}
                    <Box
                      sx={{
                        position: "absolute",
                        top: -10,
                        right: -10,
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        bgcolor: "#0f172a",
                        color: "white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 900,
                        fontSize: "1.2rem",
                        border: "3px solid white",
                      }}
                    >
                      {workflow.step}
                    </Box>
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 900, mb: 2, color: "#0f172a" }}>
                    {workflow.title}
                  </Typography>
                  <Typography variant="body1" sx={{ color: "#64748b", lineHeight: 1.7, px: 1 }}>
                    {workflow.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>

      {/* Modern Testimonials Section */}
      <Box sx={{ bgcolor: "#f8fafc", py: { xs: 10, md: 15 } }}>
        <Container>
          <Box sx={{ textAlign: "center", mb: 10 }}>
            <Chip
              icon={<Favorite sx={{ fontSize: 18, color: "#ec4899 !important" }} />}
              label="CLIENT SUCCESS"
              sx={{
                bgcolor: "#fce7f3",
                color: "#ec4899",
                fontWeight: 700,
                mb: 3,
                border: "2px solid #fbcfe8",
                px: 2,
                py: 2.5,
              }}
            />
            <Typography
              variant="h2"
              sx={{
                fontWeight: 900,
                mb: 2,
                color: "#0f172a",
                fontSize: { xs: "2.5rem", md: "4rem" },
                letterSpacing: "-0.02em",
              }}
            >
              Loved by Professionals
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: "#64748b",
                maxWidth: 700,
                mx: "auto",
                fontWeight: 400,
                fontSize: { xs: "1.1rem", md: "1.3rem" },
              }}
            >
              See how teams are transforming their contact management
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {testimonials.map((testimonial, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 5,
                    borderRadius: 4,
                    bgcolor: "white",
                    border: "2px solid #e2e8f0",
                    height: "100%",
                    position: "relative",
                    overflow: "hidden",
                    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow: "0 30px 80px rgba(14,165,233,0.15)",
                      borderColor: "#0ea5e9",
                    },
                    "&::before": {
                      content: '"\\201C"',
                      position: "absolute",
                      top: 20,
                      right: 30,
                      fontSize: "10rem",
                      color: "#f1f5f9",
                      fontFamily: "Georgia, serif",
                      lineHeight: 1,
                      zIndex: 0,
                    },
                  }}
                >
                  <Box sx={{ position: "relative", zIndex: 1 }}>
                    <Box sx={{ display: "flex", gap: 0.5, mb: 3 }}>
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} sx={{ fontSize: 24, color: "#fbbf24" }} />
                      ))}
                    </Box>

                    <Typography
                      variant="body1"
                      sx={{
                        color: "#1e293b",
                        mb: 4,
                        lineHeight: 1.8,
                        fontSize: "1.1rem",
                        fontWeight: 400,
                      }}
                    >
                      {testimonial.text}
                    </Typography>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 4 }}>
                      <Avatar
                        sx={{
                          width: 56,
                          height: 56,
                          background: "linear-gradient(135deg, #0ea5e9, #0284c7)",
                          fontWeight: 800,
                          fontSize: "1.3rem",
                        }}
                      >
                        {testimonial.avatar}
                      </Avatar>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: "#0f172a", mb: 0.5 }}>
                          {testimonial.name}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 500 }}>
                          {testimonial.role}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#94a3b8", fontSize: "0.85rem" }}>
                          {testimonial.company}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Modern Call to Action Section */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #d946ef 100%)",
          py: { xs: 8, sm: 10, md: 18 },
          px: { xs: 2, sm: 3 },
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Animated Background Elements */}
        <Box
          sx={{
            position: "absolute",
            width: "100%",
            height: "100%",
            opacity: 0.1,
          }}
        >
          {[...Array(20)].map((_, i) => (
            <Box
              key={i}
              sx={{
                position: "absolute",
                width: `${Math.random() * 100 + 50}px`,
                height: `${Math.random() * 100 + 50}px`,
                borderRadius: "50%",
                background: "white",
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                animation: `floatRandom ${Math.random() * 10 + 10}s infinite ease-in-out`,
                animationDelay: `${Math.random() * 5}s`,
                "@keyframes floatRandom": {
                  "0%, 100%": { transform: "translate(0, 0)" },
                  "50%": {
                    transform: `translate(${Math.random() * 100 - 50}px, ${Math.random() * 100 - 50}px)`,
                  },
                },
              }}
            />
          ))}
        </Box>

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 2, textAlign: "center" }}>
          <Grow in timeout={800}>
            <Box>
              <Box
                sx={{
                  width: { xs: 80, sm: 100, md: 120 },
                  height: { xs: 80, sm: 100, md: 120 },
                  borderRadius: "50%",
                  background: "rgba(255,255,255,0.2)",
                  backdropFilter: "blur(20px)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: { xs: 3, sm: 4 },
                  border: "3px solid rgba(255,255,255,0.3)",
                }}
              >
                <ContactMail sx={{ fontSize: { xs: 45, sm: 60, md: 70 }, color: "white" }} />
              </Box>

              <Typography
                variant="h2"
                sx={{
                  fontWeight: 900,
                  color: "white",
                  mb: { xs: 2, sm: 3 },
                  fontSize: { xs: "2rem", sm: "3rem", md: "4.5rem" },
                  letterSpacing: "-0.02em",
                  lineHeight: { xs: 1.2, md: 1.1 },
                }}
              >
                Ready to Transform Your
                <Box component="span" sx={{ display: "block" }}>
                  Contact Management?
                </Box>
              </Typography>

              <Typography
                variant="h5"
                sx={{
                  color: "rgba(255,255,255,0.95)",
                  mb: { xs: 4, sm: 5, md: 6 },
                  maxWidth: 800,
                  mx: "auto",
                  lineHeight: { xs: 1.6, md: 1.7 },
                  fontSize: { xs: "1rem", sm: "1.2rem", md: "1.5rem" },
                  fontWeight: 400,
                  px: { xs: 2, sm: 0 },
                }}
              >
                Join 25,000+ professionals who trust us to manage their most important relationships.
                Start your free trial today — no credit card required.
              </Typography>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={{ xs: 2, sm: 3 }}
                justifyContent="center"
                sx={{ mb: { xs: 6, sm: 7, md: 8 } }}
              >
                <Button
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForward sx={{ fontSize: { xs: 18, sm: 20 } }} />}
                  onClick={() => window.open('https://www.orucore.com', '_blank')}
                  sx={{
                    bgcolor: "white",
                    color: "#6366f1",
                    px: { xs: 5, sm: 6 },
                    py: { xs: 2, sm: 2.5 },
                    fontSize: { xs: "1rem", sm: "1.1rem", md: "1.2rem" },
                    fontWeight: 800,
                    borderRadius: 2,
                    textTransform: "none",
                    boxShadow: "0 25px 70px rgba(0,0,0,0.3)",
                    "&:hover": {
                      bgcolor: "#fbbf24",
                      color: "#1e293b",
                      transform: "translateY(-4px)",
                      boxShadow: "0 35px 90px rgba(0,0,0,0.4)",
                    },
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  }}
                >
                  Start Free Trial
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<Phone sx={{ fontSize: { xs: 18, sm: 20 } }} />}
                  onClick={() => window.open('https://www.orucore.com', '_blank')}
                  sx={{
                    borderColor: "white",
                    color: "white",
                    px: { xs: 5, sm: 6 },
                    py: { xs: 2, sm: 2.5 },
                    fontSize: { xs: "1rem", sm: "1.1rem", md: "1.2rem" },
                    fontWeight: 700,
                    borderRadius: 2,
                    textTransform: "none",
                    borderWidth: 2,
                    backdropFilter: "blur(10px)",
                    bgcolor: "rgba(255,255,255,0.1)",
                    "&:hover": {
                      borderColor: "white",
                      bgcolor: "rgba(255,255,255,0.2)",
                      borderWidth: 2,
                      transform: "translateY(-4px)",
                    },
                    transition: "all 0.3s",
                  }}
                >
                  Talk to Sales
                </Button>
              </Stack>

              <Grid container spacing={{ xs: 3, sm: 4 }} sx={{ maxWidth: 1000, mx: "auto" }}>
                <Grid item xs={6} md={3}>
                  <Box>
                    <Typography variant="h3" sx={{ fontWeight: 900, color: "white", mb: 1, fontSize: { xs: "1.8rem", sm: "2.5rem", md: "3rem" } }}>
                      14 Days
                    </Typography>
                    <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.9)", fontWeight: 500, fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1rem" } }}>
                      Free Trial
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Box>
                    <Typography variant="h3" sx={{ fontWeight: 900, color: "white", mb: 1, fontSize: { xs: "1.8rem", sm: "2.5rem", md: "3rem" } }}>
                      24/7
                    </Typography>
                    <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.9)", fontWeight: 500, fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1rem" } }}>
                      Support
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Box>
                    <Typography variant="h3" sx={{ fontWeight: 900, color: "white", mb: 1, fontSize: { xs: "1.8rem", sm: "2.5rem", md: "3rem" } }}>
                      No CC
                    </Typography>
                    <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.9)", fontWeight: 500, fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1rem" } }}>
                      Required
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Box>
                    <Typography variant="h3" sx={{ fontWeight: 900, color: "white", mb: 1, fontSize: { xs: "1.8rem", sm: "2.5rem", md: "3rem" } }}>
                      98%
                    </Typography>
                    <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.9)", fontWeight: 500, fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1rem" } }}>
                      Satisfaction
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Grow>
        </Container>
      </Box>

      {/* Footer Section */}
      <Footer />
    </Box>
  );
};

export default LandingPage;
